import { useEffect, useState } from "react";

import {
    Dialog,
    DialogContent,
    IconButton
} from "@mui/material";

import OpenInFullIcon from "@mui/icons-material/OpenInFull";

import {
    MapContainer,
    TileLayer,
    Marker,
    Popup,
    useMap
} from "react-leaflet";

import L from "leaflet";

function ResizeFix() {
    const map = useMap();

    useEffect(() => {
        const timer = setTimeout(() => {
            map.invalidateSize();
        }, 300);

        return () => clearTimeout(timer);
    }, [map]);

    return null;
}

function FollowTrain({ position, zoom }) {
    const map = useMap();

    useEffect(() => {
        if (
            Number.isFinite(position[0]) &&
            Number.isFinite(position[1])
        ) {
            map.setView(position, zoom, {
                animate: true
            });
        }
    }, [map, position, zoom]);

    return null;
}

const trainIcon = L.divIcon({
    html: `
      <div style="
        width:36px;
        height:36px;
        border-radius:50%;
        background:#2563eb;
        display:flex;
        align-items:center;
        justify-content:center;
        border:3px solid white;
        box-shadow:0 4px 10px rgba(0,0,0,.25);
        color:white;
        font-size:18px;
      ">
        🚆
      </div>
    `,
    className: "",
    iconSize: [36, 36]
});

function formatNumber(value, decimals = 6) {
    const num = Number(value);

    if (!Number.isFinite(num)) return "--";

    const [intPart, decPart = ""] = String(num).split(".");

    if (decPart.length <= decimals) return String(num);

    return `${intPart}.${decPart.slice(0, decimals)}...`;
}

export default function TrainMiniMap({ gps }) {

    const [open, setOpen] = useState(false);

    const trainPosition = [
        Number(gps?.latitude) || 0,
        Number(gps?.longitude) || 0
    ];

    const hasGps =
        Number.isFinite(trainPosition[0]) &&
        Number.isFinite(trainPosition[1]) &&
        !(trainPosition[0] === 0 &&
            trainPosition[1] === 0);

    return (
        <>
            <div
                style={{
                    background: "#fff",
                    border: "1px solid #d1d5db",
                    borderRadius: 12,
                    padding: 12
                }}
            >
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 8
                    }}
                >
                    <div>
                        <div
                            style={{
                                fontWeight: 700,
                                fontSize: 16
                            }}
                        >
                            Localização do Trem
                        </div>

                        <div
                            style={{
                                fontSize: 12,
                                color: "#6b7280"
                            }}
                        >
                            GPS em tempo real
                        </div>
                    </div>

                    <IconButton
                        size="small"
                        onClick={() => setOpen(true)}
                    >
                        <OpenInFullIcon />
                    </IconButton>
                </div>

                <div
                    style={{
                        height: 160,
                        borderRadius: 10,
                        overflow: "hidden"
                    }}
                >
                    {hasGps && (
                        <MapContainer
                            center={trainPosition}
                            zoom={15}
                            zoomControl={false}
                            dragging={false}
                            scrollWheelZoom={false}
                            doubleClickZoom={false}
                            touchZoom={false}
                            style={{
                                width: "100%",
                                height: "100%"
                            }}
                        >
                            <FollowTrain
                                position={trainPosition}
                                zoom={15}
                            />

                            <TileLayer
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />

                            <Marker
                                position={trainPosition}
                                icon={trainIcon}
                            >
                                <Popup>Trem</Popup>
                            </Marker>
                        </MapContainer>
                    )}
                </div>

                <div
                    style={{
                        marginTop: 10,
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 12
                    }}
                >
                    <span>
                        Precisão: {formatNumber(gps.accuracy, 1)} m
                    </span>

                    <span>
                        {formatNumber(gps.speed, 1)} km/h
                    </span>
                </div>
            </div>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogContent
                    style={{
                        padding: 0,
                        height: "75vh"
                    }}
                >
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 300px",
                            height: "100%"
                        }}
                    >
                        <div style={{ height: "100%" }}>
                            {hasGps && (
                                <MapContainer
                                    center={trainPosition}
                                    zoom={16}
                                    style={{
                                        width: "100%",
                                        height: "100%"
                                    }}
                                >
                                    <ResizeFix />

                                    <FollowTrain
                                        position={trainPosition}
                                        zoom={16}
                                    />

                                    <TileLayer
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    />

                                    <Marker
                                        position={trainPosition}
                                        icon={trainIcon}
                                    >
                                        <Popup>Trem</Popup>
                                    </Marker>
                                </MapContainer>
                            )}
                        </div>

                        <div
                            style={{
                                borderLeft: "1px solid #e5e7eb",
                                padding: 20,
                                background: "#fafafa"
                            }}
                        >
                            <h2 style={{ marginTop: 0 }}>
                                GPS
                            </h2>

                            <div
                                style={{
                                    color: "#666",
                                    marginBottom: 20
                                }}
                            >
                                Dados recebidos da telemetria
                            </div>

                            <div
                                style={{
                                    display: "grid",
                                    gap: 14
                                }}
                            >
                                <div>
                                    <strong>Velocidade</strong>
                                    <br />
                                    {formatNumber(gps.speed, 1)} km/h
                                </div>

                                <div>
                                    <strong>Latitude</strong>
                                    <br />
                                    {formatNumber(gps.latitude, 6)}
                                </div>

                                <div>
                                    <strong>Longitude</strong>
                                    <br />
                                    {formatNumber(gps.longitude, 6)}
                                </div>

                                <div>
                                    <strong>Altitude</strong>
                                    <br />
                                    {formatNumber(gps.altitude, 1)} m
                                </div>

                                <div>
                                    <strong>Direção</strong>
                                    <br />
                                    {formatNumber(gps.angle, 1)}°
                                </div>

                                <div>
                                    <strong>Precisão</strong>
                                    <br />
                                    {formatNumber(gps.accuracy, 1)} m
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
